import { useCallback, useEffect, useState } from 'react';

import { Box } from '@mui/material';
import CheckboxSetting from '../atoms/CheckboxSetting';
import CreditCodesEnum from '@settings/models/credits-codes.enum';
import JNumberField from '@atoms/JNumberField';
import JTextFieldBase from '@atoms/JTextFieldBase';
import Jpaper from '@atoms/JPaper';
import { Loading } from '@atoms/Loading';
import NumberSetting from '../atoms/NumberSetting';
import UpdateSettingDto from '@settings/models/dto/update-setting.dto';
import { useGetCreditsSettings } from '@settings/queries/useGetSettings';
import useJConfirmationBar from '@/hooks/useJConfirmationBar';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useUpdateSettings } from '@settings/mutations/settingsMutations';

const CreditsSettings = () => {
  const { data, isFetching: isSettingsFetching } = useGetCreditsSettings();
  const { t } = useTranslation();
  const {
    mutate: mutateSettings,
    isLoading: isMutateSettingsLoading,
    isSuccess: isMutateSettingsSuccess,
  } = useUpdateSettings();
  const { show, close } = useJConfirmationBar();
  const queryClient = useQueryClient();
  const [isDirty, setIsDirty] = useState(false);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [payload, setPayload] = useState<UpdateSettingDto[]>([]);
  const settings = data?.data;
  const getValueByCode = (code: CreditCodesEnum): string | undefined => {
    return settings?.find((item) => item.code === code)?.value;
  };

  const getDefaultValueByCode = (code: CreditCodesEnum): string | undefined => {
    return settings?.find((item) => item.code === code)?.defaultValue;
  };

  const onChangeSetting = (code: CreditCodesEnum, value: string) => {
    // set is dirty
    if (!isDirty) {
      setIsDirty(true);
    }
    // Check if the code already exists in payload
    const existingIndex = payload.findIndex((item) => item.code === code);
    if (existingIndex !== -1) {
      updateValue(code, value, existingIndex);
    } else {
      addValue(code, value);
    }
  };

  const addValue = (code: CreditCodesEnum, value: string) => {
    setPayload((prev) => [...prev, { code, value }]);
  };
  const updateValue = (code: CreditCodesEnum, value: string, index: number) => {
    const updatedPayload = [...payload];
    updatedPayload[index] = { code, value };
    setPayload(updatedPayload);
  };

  useEffect(() => {
    if (!isDirty) return;
    show({
      onClose: () => {
        queryClient.invalidateQueries(['settings-general']);
      },
      onConfirm: () => mutateSettings({ payload }),
    });
  }, [isDirty, payload]);

  useEffect(() => {
    return () => close();
  }, []);

  useEffect(() => {
    if (!isMutateSettingsSuccess) return;
    close();
  }, [isMutateSettingsSuccess]);

  const isFieldHidden = useCallback(
    (label: string, rank: number): boolean => {
      if (rank.toLocaleString() === searchText) return false;
      return Boolean(
        searchText &&
          !label.toLocaleLowerCase()?.includes(searchText.toLocaleLowerCase()),
      );
    },
    [searchText],
  );
  const usingCreditWatchCvHidden = isFieldHidden(
    t('USING_CREDITS_TO_WATCH_CV_VIDEO'),
    1,
  );
  const usingCreditUnlockContactHidden = isFieldHidden(
    t('USING_CREDITS_TO_UNLOCK_CONTACT'),
    2,
  );
  const costOfExpensesHidden = isFieldHidden(t('COST_OF_EXPENSES'), 3);
  const timeoutHidden = isFieldHidden(t('TIMEOUT_IN_DAYS'), 4);
  const defaultThresholdHidden = isFieldHidden(t('DEFAULT_THRESHOLD'), 5);

  const notificationToShareCreatedTopUpHidden = isFieldHidden(
    t('NOTIFICATION_TO_SHARE_CREATED_TOP_UP'),
    6,
  );
  const emailToShareCreatedTopUpHidden = isFieldHidden(
    t('EMAIL_TO_SHARE_CREATED_TOP_UP'),
    7,
  );
  const notificationForShareTopUpHidden = isFieldHidden(
    t('NOTIFICATION_FOR_SHARED_TOP_UP'),
    8,
  );
  const notificationForThresholdHidden = isFieldHidden(
    t('NOTIFICATION_FOR_THRESHOLD'),
    9,
  );
  const emailForThresholdHidden = isFieldHidden(t('EMAIL_FOR_THRESHOLD'), 10);
  const displayCreditSettings = !(
    usingCreditUnlockContactHidden &&
    usingCreditWatchCvHidden &&
    costOfExpensesHidden &&
    timeoutHidden &&
    defaultThresholdHidden
  );

  const displayContactCreditSettings = !(
    notificationToShareCreatedTopUpHidden &&
    emailToShareCreatedTopUpHidden &&
    notificationForShareTopUpHidden &&
    notificationForThresholdHidden &&
    emailForThresholdHidden
  );

  return (
    <>
      {(isMutateSettingsLoading || isSettingsFetching) ?? <Loading />}
      <Box mb={3}>
        <JTextFieldBase
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          attr={{ whiteBg: true }}
          label={t('SEARCH_SETTING')}
        />
      </Box>
      {!displayCreditSettings && !displayContactCreditSettings && (
        <Jpaper>{t('NOT_SETTINGS_FOUND')}</Jpaper>
      )}
      {displayCreditSettings && (
        <Jpaper mb={3}>
          <CheckboxSetting
            hidden={usingCreditWatchCvHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(CreditCodesEnum.CreditToWatchCvVideo) ??
              'true'
            }
            newValue={getValueByCode(CreditCodesEnum.CreditToWatchCvVideo)}
            rank={1}
            label={t('USING_CREDITS_TO_WATCH_CV_VIDEO')}
            items={[
              { label: t('YES'), value: 'true' },
              { label: t('NO'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(
                CreditCodesEnum.CreditToWatchCvVideo,
                String(value),
              )
            }
          />

          <CheckboxSetting
            hidden={usingCreditUnlockContactHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(CreditCodesEnum.CreditToUnlockContact) ??
              'true'
            }
            newValue={getValueByCode(CreditCodesEnum.CreditToUnlockContact)}
            rank={2}
            label={t('USING_CREDITS_TO_UNLOCK_CONTACT')}
            items={[
              { label: t('YES'), value: 'true' },
              { label: t('NO'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(
                CreditCodesEnum.CreditToUnlockContact,
                String(value),
              )
            }
          />

          <NumberSetting
            hidden={costOfExpensesHidden}
            label={t('COST_OF_EXPENSES')}
            rank={3}
          >
            <JNumberField
              reset={isSettingsFetching}
              size="medium"
              name="setting-field"
              defaultValue={getDefaultValueByCode(
                CreditCodesEnum.CostToWatchCvVideo,
              )}
              newValue={getValueByCode(CreditCodesEnum.CostToWatchCvVideo)}
              onNumberChange={(value) => {
                onChangeSetting(
                  CreditCodesEnum.CostToWatchCvVideo,
                  String(value),
                );
              }}
              label={t('READ_A_CV_VIDEO')}
            />
            <JNumberField
              reset={isSettingsFetching}
              size="medium"
              name="setting-field"
              defaultValue={getDefaultValueByCode(
                CreditCodesEnum.CostToUnlockContact,
              )}
              newValue={getValueByCode(CreditCodesEnum.CostToUnlockContact)}
              onNumberChange={(value) => {
                onChangeSetting(
                  CreditCodesEnum.CostToUnlockContact,
                  String(value),
                );
              }}
              label={t('UNLOCK_A_CONTACT')}
            />
          </NumberSetting>
          <NumberSetting
            hidden={timeoutHidden}
            label={t('TIMEOUT_IN_DAYS')}
            rank={4}
          >
            <JNumberField
              reset={isSettingsFetching}
              size="medium"
              name="setting-field"
              defaultValue={getDefaultValueByCode(CreditCodesEnum.TimeoutDay)}
              newValue={getValueByCode(CreditCodesEnum.TimeoutDay)}
              onNumberChange={(value) => {
                onChangeSetting(CreditCodesEnum.TimeoutDay, String(value));
              }}
              label={t('NUMBER_OF_DAYS')}
            />
          </NumberSetting>
          <NumberSetting
            hidden={defaultThresholdHidden}
            label={t('DEFAULT_THRESHOLD_CREDIT')}
            rank={5}
          >
            <JNumberField
              reset={isSettingsFetching}
              size="medium"
              name="setting-field"
              defaultValue={getDefaultValueByCode(
                CreditCodesEnum.DefaultThreshold,
              )}
              newValue={getValueByCode(CreditCodesEnum.DefaultThreshold)}
              onNumberChange={(value) => {
                onChangeSetting(
                  CreditCodesEnum.DefaultThreshold,
                  String(value),
                );
              }}
              label={t('DEFAULT_THRESHOLD')}
            />
          </NumberSetting>
        </Jpaper>
      )}
      {displayContactCreditSettings && (
        <Jpaper mb={3}>
          <CheckboxSetting
            hidden={notificationToShareCreatedTopUpHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(
                CreditCodesEnum.NotificationToShareTopUpCreated,
              ) ?? 'true'
            }
            newValue={getValueByCode(
              CreditCodesEnum.NotificationToShareTopUpCreated,
            )}
            rank={6}
            label={t('NOTIFICATION_TO_SHARE_CREATED_TOP_UP')}
            items={[
              { label: t('YES'), value: 'true' },
              { label: t('NO'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(
                CreditCodesEnum.NotificationToShareTopUpCreated,
                String(value),
              )
            }
          />
          <CheckboxSetting
            hidden={emailToShareCreatedTopUpHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(CreditCodesEnum.EmailToShareTopUpCreated) ??
              'true'
            }
            newValue={getValueByCode(CreditCodesEnum.EmailToShareTopUpCreated)}
            rank={7}
            label={t('EMAIL_TO_SHARE_CREATED_TOP_UP')}
            items={[
              { label: t('YES'), value: 'true' },
              { label: t('NO'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(
                CreditCodesEnum.EmailToShareTopUpCreated,
                String(value),
              )
            }
          />
          <CheckboxSetting
            hidden={notificationForShareTopUpHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(CreditCodesEnum.NotificationShareDone) ??
              'true'
            }
            newValue={getValueByCode(CreditCodesEnum.NotificationShareDone)}
            rank={8}
            label={t('NOTIFICATION_FOR_SHARED_TOP_UP')}
            items={[
              { label: t('YES'), value: 'true' },
              { label: t('NO'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(
                CreditCodesEnum.NotificationShareDone,
                String(value),
              )
            }
          />
          <CheckboxSetting
            hidden={notificationForThresholdHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(CreditCodesEnum.NotificationThreshold) ??
              'true'
            }
            newValue={getValueByCode(CreditCodesEnum.NotificationThreshold)}
            rank={9}
            label={t('NOTIFICATION_FOR_THRESHOLD')}
            items={[
              { label: t('YES'), value: 'true' },
              { label: t('NO'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(
                CreditCodesEnum.NotificationThreshold,
                String(value),
              )
            }
          />
          <CheckboxSetting
            hidden={emailForThresholdHidden}
            reset={isSettingsFetching}
            defaultValue={
              getDefaultValueByCode(CreditCodesEnum.EmailThreshold) ?? 'true'
            }
            newValue={getValueByCode(CreditCodesEnum.EmailThreshold)}
            rank={10}
            label={t('EMAIL_FOR_THRESHOLD')}
            items={[
              { label: t('YES'), value: 'true' },
              { label: t('NO'), value: 'false' },
            ]}
            onChange={(value) =>
              onChangeSetting(CreditCodesEnum.EmailThreshold, String(value))
            }
          />
        </Jpaper>
      )}
    </>
  );
};

export default CreditsSettings;
